import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
} from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useEntityInspector } from '../../context/EntityInspectorContext';
import EntityRef from './EntityRef';
import { entityFrontendPath } from '../../lib/entityNavigation';
import { parseEntityId } from '../../lib/entityIds';
import { entityTypeColor, entityTypeIcon, entityTypeLabel } from '../../lib/entityMeta';
import { useEntityInspect } from '../../services/api';
import { KALEIDOSCOPE } from '../../theme/kaleidoscope';
import {
  ErrorState,
  InspectorEntry,
  LoadingState,
  SectionHeader,
  StatusBadge,
  Surface,
} from '../../design-system/v2';
import { safeAlpha } from '../../design-system/tokens';
import { EnsBar } from '../common/EnsLoader';

/** snake_case / lowercase registry values → sentence case labels. */
function humanize(value: string | undefined | null): string {
  if (!value) return '';
  const text = value.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function LinkedSection({
  title,
  links,
  projectId,
}: {
  title: string;
  links: Array<{ entity_id: string; label?: string; rel?: string }>;
  projectId: string;
}) {
  if (!links.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <SectionHeader title={title} compact />
      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
        {links.map((link) => (
          <EntityRef
            key={link.entity_id}
            entityId={link.entity_id}
            projectId={projectId}
            label={link.label}
            variant="chip"
            size="small"
          />
        ))}
      </Stack>
    </Box>
  );
}

const UniversalInspectorDrawer: React.FC = () => {
  const { open, entityId, projectId, fallback, closeInspector } = useEntityInspector();

  const { data, isLoading, isError, error, isFetching } = useEntityInspect(
    open ? entityId ?? undefined : undefined,
    projectId ?? '',
  );

  const parsed = entityId ? parseEntityId(entityId) : null;
  const type = data?.entity.type ?? fallback?.type ?? parsed?.type ?? 'entity';
  const color = entityTypeColor(type);
  const displayName =
    data?.entity.display_name ?? fallback?.label ?? parsed?.id ?? entityId ?? 'Entity';
  const displayStatus = data?.entity.status ?? fallback?.status;
  const pid = projectId ?? data?.entity.project_id ?? '';

  const frontendPath = entityId && pid ? entityFrontendPath(entityId, pid) : undefined;

  const metadataEntries = useMemo(() => {
    const meta = data?.entity.metadata;
    if (!meta) return [];
    return Object.entries(meta)
      .filter(([, v]) => v != null && v !== '')
      .slice(0, 8);
  }, [data]);

  const relationships = data?.relationships ?? [];
  const linked = data?.linked;
  const actions = data?.available_actions ?? [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={closeInspector}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          bgcolor: KALEIDOSCOPE.surface,
          borderLeft: `1px solid ${KALEIDOSCOPE.border}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Surface
          level="flat"
          pad="md"
          sx={{
            borderRadius: 0,
            height: 'auto',
            borderBottom: `1px solid ${KALEIDOSCOPE.border}`,
            bgcolor: alpha(color, 0.06),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                bgcolor: alpha(color, 0.12),
                flexShrink: 0,
              }}
            >
              {entityTypeIcon(type)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SectionHeader
                label={entityTypeLabel(type)}
                title={displayName}
                metrics={
                  displayStatus ? <StatusBadge label={humanize(displayStatus)} tone="info" /> : undefined
                }
                compact
              />
              {entityId && (
                <InspectorEntry label="Entity id" value={entityId} mono />
              )}
            </Box>
            <IconButton size="small" onClick={closeInspector} aria-label="Close inspector">
              <Close fontSize="small" />
            </IconButton>
          </Box>
          {(isLoading || isFetching) && (
            <EnsBar
              sx={{
                mt: 1.5,
                height: 2,
                bgcolor: alpha(color, 0.1),
                '& .MuiLinearProgress-bar': { bgcolor: color },
              }}
            />
          )}
        </Surface>

        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
          {isError && (
            <ErrorState
              title="Registry lookup failed"
              message={
                (error as Error)?.message?.includes('404') || (error as Error)?.message?.includes('not')
                  ? 'Entity not found in registry — showing local reference.'
                  : 'Could not load entity from registry.'
              }
            />
          )}

          {isLoading && !data ? (
            <LoadingState rows={3} label="Loading entity…" />
          ) : (
            <>
              {data?.entity.origin?.system && (
                <InspectorEntry
                  label="Origin"
                  value={`${data.entity.origin.system}${data.entity.origin.source ? ` · ${data.entity.origin.source}` : ''}`}
                />
              )}

              {metadataEntries.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <SectionHeader title="Details" compact />
                  {metadataEntries.map(([key, value]) => (
                    <InspectorEntry
                      key={key}
                      label={humanize(key)}
                      value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      mono={typeof value !== 'object'}
                    />
                  ))}
                </Box>
              )}

              {relationships.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <SectionHeader title="Relationships" compact />
                  <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                    {relationships.map((rel) => (
                      <Box
                        key={`${rel.direction}-${rel.entity_id}-${rel.relation}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.5,
                          px: 1,
                          borderRadius: 1,
                          '&:hover': { bgcolor: safeAlpha(KALEIDOSCOPE.ink, 0.04) },
                        }}
                      >
                        <StatusBadge label={humanize(rel.direction)} tone="neutral" />
                        <EntityRef
                          entityId={rel.entity_id}
                          projectId={pid}
                          label={rel.label}
                          variant="inline"
                        />
                        <StatusBadge label={humanize(rel.relation)} tone="neutral" />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {linked && pid && (
                <>
                  <LinkedSection title="Investigations" links={linked.investigations ?? []} projectId={pid} />
                  <LinkedSection title="Incidents" links={linked.incidents ?? []} projectId={pid} />
                  <LinkedSection title="Change requests" links={linked.changes ?? []} projectId={pid} />
                  <LinkedSection title="Repositories" links={linked.repositories ?? []} projectId={pid} />
                  <LinkedSection title="Services" links={linked.services ?? []} projectId={pid} />
                  <LinkedSection title="Documentation" links={linked.documentation ?? []} projectId={pid} />
                  <LinkedSection title="Evidence" links={linked.evidence ?? []} projectId={pid} />
                </>
              )}

              {actions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <SectionHeader title="Actions" compact />
                  <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                    {actions.map((action) => {
                      const path = action.http_path?.startsWith('/api/v2/entities/')
                        ? undefined
                        : frontendPath;
                      return path ? (
                        <Button
                          key={action.id}
                          component={RouterLink}
                          to={path}
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                          onClick={closeInspector}
                        >
                          {action.label}
                        </Button>
                      ) : (
                        <InspectorEntry
                          key={action.id}
                          label={action.label}
                          value={
                            action.http_method && action.http_path
                              ? `${action.http_method} ${action.http_path}`
                              : '—'
                          }
                          mono
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Box>

        {frontendPath && (
          <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${KALEIDOSCOPE.border}` }}>
            <Button
              component={RouterLink}
              to={frontendPath}
              fullWidth
              variant="contained"
              endIcon={<OpenInNew />}
              onClick={closeInspector}
              sx={{ textTransform: 'none' }}
            >
              Open in workspace
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default UniversalInspectorDrawer;
